/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	cloneElement,
	type ComponentType,
	forwardRef,
	isValidElement,
	memo,
	useCallback,
} from 'react';

import { cssMap as cssMapUnbound, jsx } from '@compiled/react';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import type { AvatarPropTypes } from '@atlaskit/avatar/avatar';
import StatusVerifiedIcon from '@atlaskit/icon/core/status-verified';
import type { TeamAvatarProps } from '@atlaskit/teams-avatar/teams-avatar';
import { token } from '@atlaskit/tokens';

import { LinkWrapper } from './link-wrapper';
import { RemovableWrapper } from './removable-wrapper';
import { useButtonInteraction } from './use-button-interaction';
import { useLink } from './use-link';
import { useRemoveButton } from './use-remove-button';
import { useTagRemoval } from './use-tag-removal';

/**
 * The type values that AvatarTag accepts.
 */
export type TypesOfAvatars = 'user' | 'agent' | 'other';

/**
 * Props controlled by AvatarTag that will be passed to the avatar component.
 * These props are managed internally by AvatarTag.
 */
export interface AvatarRenderProps {
	/**
	 * The size of the avatar. Always 'xxsmall' for AvatarTag.
	 */
	size: 'xxsmall';
	/**
	 * The appearance/shape of the avatar based on the tag type.
	 * - 'circle' for user (round avatars)
	 * - 'square' for other/teams (square avatars)
	 * - 'hexagon' for agents (hexagonal avatars)
	 */
	appearance: 'circle' | 'square' | 'hexagon';
	/**
	 * The border color for the avatar. Always 'transparent' for AvatarTag.
	 */
	borderColor: 'transparent';
}

/**
 * Common props shared across all AvatarTag types.
 */
interface CommonAvatarTagProps {
	/**
	 * Text to be displayed in the tag (usually a person's name or entity name).
	 */
	// eslint-disable-next-line @repo/internal/react/consistent-props-definitions
	text: string;
	/**
	 * URI or path. If provided, the tag will be a link.
	 */
	href?: string;
	/**
	 * A link component to be used instead of our standard link. The styling of
	 * our link item will be applied to the link that is passed in.
	 */
	linkComponent?: ComponentType<any>;
	/**
	 * A `testId` prop is provided for specified elements.
	 */
	testId?: string;
	/**
	 * Flag to indicate if a tag is removable. Defaults to true.
	 */
	isRemovable?: boolean;
	/**
	 * Text rendered as the aria-label for remove button.
	 */
	removeButtonLabel?: string;
	/**
	 * Handler to be called before the tag is removed. If it does not return a
	 * truthy value, the tag will not be removed.
	 */
	onBeforeRemoveAction?: () => boolean;
	/**
	 * Handler to be called after tag is removed.
	 */
	onAfterRemoveAction?: (text: string) => void;
	/**
	 * Maximum width of the tag. When exceeded, the text will be truncated with ellipsis.
	 * Accepts any valid CSS max-width value (e.g., '200px', '15rem', '100%').
	 */
	maxWidth?: string | number;
	/**
	 * When false, removes the tag's default margin. Use in contexts like User-picker
	 * where the parent controls spacing. Defaults to `true`.
	 */
	hasMargin?: boolean;
	/**
	 * Handler called when the tag is clicked. Only fires for link tags (when href is provided).
	 * The second argument provides an Atlaskit UI analytics event.
	 */
	onClick?: (e: React.MouseEvent<HTMLAnchorElement>, analyticsEvent: UIAnalyticsEvent) => void;
}

/**
 * Props for user avatar tags with circular avatars.
 */
type UserAvatarTag = CommonAvatarTagProps & {
	/**
	 * The type of avatar tag. 'user' uses circular avatars for individuals.
	 */
	type: 'user';
	/**
	 * isVerified is not allowed for user tags.
	 */
	isVerified?: never;
	/**
	 * The avatar component to render. AvatarTag will provide controlled props (size, appearance, borderColor).
	 * Accepts Avatar from `@atlaskit/avatar` package
	 */
	avatar: ComponentType<AvatarPropTypes>;
};

/**
 * Props for other/teams avatar tags with square avatars.
 */
type OtherAvatarTag = CommonAvatarTagProps & {
	/**
	 * The type of avatar tag. 'other' uses square avatars for teams/projects/spaces.
	 */
	type: 'other';
	/**
	 * Whether this entity is verified. Shows a blue verified icon after the text.
	 */
	isVerified?: boolean;
	/**
	 * The avatar component to render. AvatarTag will provide controlled props (size, appearance, borderColor).
	 * Accepts Avatar from `@atlaskit/avatar` or `@atlaskit/teams-avatar` package
	 */
	avatar: ComponentType<AvatarPropTypes> | ComponentType<TeamAvatarProps>;
};

/**
 * Props for agent avatar tags with hexagonal avatars.
 */
type AgentAvatarTag = CommonAvatarTagProps & {
	/**
	 * The type of avatar tag. 'agent' uses hexagonal avatars for AI agents.
	 */
	type: 'agent';
	/**
	 * isVerified is not allowed for agent tags.
	 */
	isVerified?: never;
	/**
	 * The avatar component to render. AvatarTag will provide controlled props (size, appearance, borderColor).
	 * Accepts Avatar from `@atlaskit/avatar` package
	 */
	avatar: ComponentType<AvatarPropTypes>;
};

/**
 * Props for AvatarTag component. Uses discriminated union based on `type`.
 */
export type AvatarTagProps = UserAvatarTag | OtherAvatarTag | AgentAvatarTag;

const styles = cssMapUnbound({
	baseStyles: {
		display: 'inline-flex',
		boxSizing: 'border-box',
		minWidth: '0px',
		maxWidth: '11.25rem',
		height: token('space.250'),
		position: 'relative',
		alignItems: 'center',
		verticalAlign: 'middle',
		gap: token('space.025'),
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.full'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		color: token('color.text'),
		cursor: 'default',
		font: token('font.body.small'),
		paddingInlineEnd: '6px',
		paddingInlineStart: token('space.0'),
		paddingBlock: token('space.0'),
		marginBlock: token('space.050'),
		marginInline: token('space.050'),
	},
	noMarginStyles: {
		marginBlock: token('space.0'),
		marginInline: token('space.0'),
	},
	otherBaseStyles: {
		borderRadius: token('radius.small'),
		paddingInlineEnd: token('space.050'),
	},
	agentBaseStyles: {
		borderRadius: token('radius.small'),
		paddingInlineEnd: token('space.050'),
	},
	removableStyles: {
		paddingInlineEnd: '3px',
		// Ensure remove button always fits when compressed; text/avatar truncate first.
		minWidth: '2.5rem',
	},
	userRemovableStyles: {
		paddingInlineEnd: '3px',
		minWidth: '2.5rem',
	},
	avatarStyles: {
		display: 'inline-flex',
		alignItems: 'center',
		flexShrink: 0,
		textDecoration: 'none',
		borderRadius: token('radius.full'),
		overflow: 'hidden',
		marginInlineStart: '-1px',
	},
	otherAvatarStyles: {
		borderRadius: token('radius.small', '4px'),
	},
	agentAvatarStyles: {
		borderRadius: 0,
	},
	textStyles: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		flex: '1 1 0',
		flexShrink: 1,
		minWidth: 0,
		color: token('color.text'),
	},
	afterStyles: {
		display: 'flex',
		alignItems: 'center',
		flex: '0 0 auto',
		flexShrink: 0,
		pointerEvents: 'auto',
		marginInlineStart: token('space.025'),
		position: 'relative',
	},
	verifiedIconStyles: {
		display: 'inline-flex',
		alignItems: 'center',
		flexShrink: 0,
		color: token('color.icon.accent.blue'),
		marginInlineStart: '1px',
	},
	focusRingStyles: {
		// Only show focus ring when keyboard navigating (not mouse clicks)
		'&:focus-visible': {
			outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
	// Show focus ring when child link is focused via keyboard (applied conditionally via JS)
	childFocusRingStyles: {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
		outlineOffset: token('space.025'),
	},
	// Base interactive styles - always applied when link (cursor, link styling)
	interactiveBaseStyles: {
		cursor: 'pointer',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& a': {
			display: 'inline-flex',
			alignItems: 'center',
			gap: token('space.025'),
			textDecoration: 'none',
			// Allow link to shrink and enable text truncation
			minWidth: 0,
			overflow: 'hidden',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			color: 'inherit !important',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			outline: 'none !important',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			boxShadow: 'none !important',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& a:focus': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			outline: 'none !important',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			boxShadow: 'none !important',
			textDecoration: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& a:visited': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			color: 'inherit !important',
		},
	},
	// Hover/active styles - only applied when NOT over the button
	interactiveHoverStyles: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& a:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			color: 'inherit !important',
		},
		// Only underline the text span, not the avatar
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& a:hover > span[data-tag-text]': {
			textDecoration: 'underline',
		},
	},
});

/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-important-styles */
// Border color style - gray only for AvatarTag.
// Uses the new color.border.accent.gray.subtle token directly so no OKLCH/color-mix
// runtime transforms are needed.
const borderColorStyles = cssMapUnbound({
	root: {
		'--tag-border-token': token('color.border.accent.gray.subtle'),
	},
});

// Border filter styles - base state
const borderFilterStyles = cssMapUnbound({
	root: {
		borderColor: 'var(--tag-border-token)',
	},
});

/* eslint-enable @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-important-styles */

/**
 * __AvatarTag__
 *
 * AvatarTag is a specialized tag for representing people, teams/other entities, or agents with an avatar and name.
 * The visual appearance (round, square, or hexagonal) is determined by the `type` prop.
 */
const AvatarTagComponent = forwardRef<HTMLSpanElement, AvatarTagProps>(function AvatarTag(
	{
		text,
		type,
		avatar: AvatarComponent,
		href,
		linkComponent,
		isRemovable = true,
		removeButtonLabel,
		onBeforeRemoveAction,
		onAfterRemoveAction,
		testId,
		isVerified, // Shows verified icon for 'other' type tags
		maxWidth,
		hasMargin = true,
		onClick,
		...other
	},
	ref,
) {
	const { status, handleRemoveRequest, onKeyPress, removingTag, showingTag } =
		useTagRemoval(onBeforeRemoveAction);

	const onShrinkOutExitComplete = useCallback(() => {
		onAfterRemoveAction?.(text);
	}, [onAfterRemoveAction, text]);

	const { isLink, LinkComponent } = useLink(href, linkComponent);
	const {
		isLinkHovered,
		isOverButton,
		isButtonFocused,
		isLinkFocused,
		buttonHandlers,
		linkHandlers,
	} = useButtonInteraction();

	// Determine styles based on type
	const isOtherType = type === 'other';
	const isAgentType = type === 'agent';
	const isUserType = type === 'user';

	// isVerified is only available for 'other' type
	const showVerified = isOtherType && isVerified;

	// Determine avatar appearance based on type
	const avatarAppearance = isUserType ? 'circle' : isAgentType ? 'hexagon' : 'square';

	// Determine remove button shape based on type
	const removeButtonShape = isUserType ? 'circle' : 'default';

	const removeButton = useRemoveButton({
		isRemovable,
		tagText: text,
		removeButtonLabel,
		testId,
		handleRemoveRequest,
		removingTag,
		showingTag,
		onKeyPress,
		shape: removeButtonShape,
		buttonHandlers,
	});

	// Render the avatar with controlled props, then clone so our props are applied
	const controlledProps: AvatarRenderProps = {
		size: 'xxsmall',
		appearance: avatarAppearance,
		borderColor: 'transparent',
	};
	const AvatarComponentTyped = AvatarComponent as unknown as ComponentType<AvatarRenderProps>;
	const rendered = <AvatarComponentTyped {...controlledProps} />;
	const avatarElement =
		// eslint-disable-next-line @repo/internal/react/no-clone-element
		isValidElement(rendered) ? cloneElement(rendered, controlledProps) : rendered;

	const tagContent = (
		<span
			{...other}
			ref={ref}
			css={[
				styles.baseStyles,
				!hasMargin && styles.noMarginStyles,
				isOtherType && styles.otherBaseStyles,
				isAgentType && styles.agentBaseStyles,
				borderColorStyles.root,
				borderFilterStyles.root,
				isLink && styles.interactiveBaseStyles,
				isLink && styles.focusRingStyles,
				// Only apply hover/active styles when link is hovered but NOT over the button
				isLink && isLinkHovered && !isOverButton && styles.interactiveHoverStyles,
				isRemovable && !isUserType && styles.removableStyles,
				isRemovable && isUserType && styles.userRemovableStyles,
				// Show focus ring when link is focused (but not when button is focused)
				isLinkFocused && !isButtonFocused && styles.childFocusRingStyles,
			]}
			data-testid={testId}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={maxWidth !== undefined ? { maxWidth } : undefined}
		>
			<LinkWrapper
				isLink={isLink}
				href={href}
				LinkComponent={LinkComponent}
				testId={testId}
				linkHandlers={linkHandlers}
				onClick={onClick}
			>
				<span
					css={[
						styles.avatarStyles,
						isOtherType && styles.otherAvatarStyles,
						isAgentType && styles.agentAvatarStyles,
					]}
				>
					{avatarElement}
				</span>
				<span css={styles.textStyles} data-tag-text>
					{text}
				</span>
			</LinkWrapper>
			{showVerified && (
				<span css={styles.verifiedIconStyles}>
					<StatusVerifiedIcon label="Verified" size="small" />
				</span>
			)}
			{removeButton && <span css={styles.afterStyles}>{removeButton}</span>}
		</span>
	);

	return (
		<RemovableWrapper
			isRemovable={isRemovable}
			status={status}
			onShrinkOutExitComplete={isRemovable ? onShrinkOutExitComplete : undefined}
		>
			{tagContent}
		</RemovableWrapper>
	);
});

/**
 * __Avatar tag__
 */
const AvatarTag: import('react').MemoExoticComponent<
	import('react').ForwardRefExoticComponent<
		AvatarTagProps & import('react').RefAttributes<HTMLSpanElement>
	>
> = memo(AvatarTagComponent);

export default AvatarTag;
